<template>
  <div class="toolbar" display="flex">
    <!--Toolbar-->
    <v-toolbar :color="colorHeader">
      <div class="w-full h-full flex-row-between ml-2">
        <!-- Left Section -->
        <div class="flex-row">
          <v-img
            :src="ownerAppIcon"
            class="cursor-pointer"
            height="56px"
            width="56px"
            @click.left.exact="goToHomeView"
            @click.middle.exact="goToHomeViewNewWindow"
            @click.ctrl.exact="goToHomeViewNewWindow"
            alt="mainImage"
          />
          <component
            :is="toolbarComponent"
            :icon="'nine-dots'"
            :ownerPrimaryColor="colorPrimary"
            :ownerOnSurfaceColor="colorOnSurface"
            :ownerSurfaceColor="colorSurface"
            :ownerHeaderColor="colorHeader"
            :ownerOnHeaderColor="colorOnHeader"
            :ownerOnPrimaryColor="colorOnPrimary"
            :personalizationToolbar="ownerToolbarColors.personalizationToolbar"
          />
          <span v-if="showConfigurationToolbar">
            <component
              :is="toolbarComponent"
              :icon="'list'"
              :ownerPrimaryColor="colorPrimary"
              :ownerOnSurfaceColor="colorOnSurface"
              :ownerSurfaceColor="colorSurface"
              :ownerHeaderColor="colorHeader"
              :ownerOnHeaderColor="colorOnHeader"
              :ownerOnPrimaryColor="colorOnPrimary"
              :personalizationToolbar="
                ownerToolbarColors.personalizationToolbar
              "
          /></span>
        </div>

        <!-- Right Section -->
        <div class="rightSection flex-col-center">
          <div class="flex-row">
            <!-- Help menu: Zoho Desk help center + support chat -->
            <v-menu v-if="isManager" offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <span
                  id="helpMenuLauncher"
                  class="cursor-pointer mr-4 my-auto"
                  v-bind="attrs"
                  v-on="on"
                >
                  <F2gIcon
                    value="question-mark"
                    :height="35"
                    :width="24"
                    :strokeWidth="4"
                    :stroke="colorOnHeader"
                  />
                </span>
              </template>
              <v-list dense :style="{ 'background-color': colorSurface }">
                <v-list-item @click="openHelpCenter">
                  <v-list-item-title :style="{ color: colorOnSurface }">
                    {{ $t("helpCenter") }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item v-if="hasPermsToChat" @click="openSalesIQChat">
                  <v-list-item-title :style="{ color: colorOnSurface }">
                    {{ $t("supportChat") }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <!-- notifications -->
            <div
              class="notificationsWrapper my-auto"
              :style="{
                'border-color': colorOnHeader,
              }"
            >
              <FirebaseNotifications
                @click="showCardNotifications"
                :ownerOnHeaderColor="colorOnHeader"
                :ownerDotColor="colorDot"
              />
            </div>
            <!-- Nombre + img-->
            <div
              class="flex-row cursor-pointer truncate pr-2 pl-4 mr-2"
              @click="toggleCardSettings"
              v-on-clickaway="closeCardSettings"
            >
              <div v-if="mobileMode" class="nameWrapper flex-col my-auto ml-2">
                <span
                  class="mr-2"
                  style="line-height: 0.8rem"
                  :style="{
                    color: colorOnHeader,
                  }"
                  >{{ userProfile.name }} {{ userProfile.surnames }}</span
                >
                <span
                  v-if="userProfile.ownerPerson"
                  class="ml-auto mr-2 text--md"
                  :style="{
                    color: colorSecondary,
                  }"
                  >{{ userProfile.ownerPerson.name }}</span
                >
              </div>
              <div class="image-container">
                <v-img class="image" :src="ownerLogo" :contain="true" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-toolbar>

    <!-- Notification List -->
    <transition name="fade">
      <v-card
        v-on-clickaway="closeNotificationList"
        v-if="showNotifications"
        elevation="8"
        class="card card--notifications"
        :style="'background-color:' + colorHeader"
      >
        <NotificationElemList
          style="margin: 10px 0"
          v-for="(notification, index) in notifications.notificationsQueue"
          v-show="index < 3"
          :key="notification.data.sending_time"
          :value="notification"
          :viewed="notification.view"
          :colorOnHeader="colorOnHeader"
          @remove="removeNotification(notification)"
          @clickNotification="showNotifications = false"
        />
        <div
          v-if="notifications.existsMoreBellNotifications"
          @click.left.exact="showMoreNotifications"
          @click.middle.exact="showMoreNotificationsNewWindow"
          @click.ctrl.exact="showMoreNotificationsNewWindow"
          class="showMore pa-3"
          :style="'color:' + colorPrimary"
        >
          {{ $t("common.showMore") }}
        </div>
        <div v-show="notifications.notificationsQueue.length == 0">
          <NoData
            :type="NoDataEnum.enum().INCIDENCES"
            small
            invertedColorTitle
            :title="$t('common.noNotifications')"
            :ownerOnSurfaceColor="colorOnSurface"
            :ownerSurfaceColor="colorSurface"
          />
        </div>
      </v-card>
    </transition>

    <!--Settings card -->
    <transition name="fade">
      <div>
        <v-card
          v-show="showSettings"
          elevation="8"
          class="card cardSettings button-rounded"
          :style="{
            'background-color': colorHeader,
            color: colorOnHeader,
          }"
        >
          <v-layout column>
            <!-- Name +  icon -->
            <div @click="cardClickEvent('card')">
              <div
                class="cardSettings-username flex-row cursor-pointer truncate"
                :style="`background-color: ${colorHeader}`"
              >
                <div class="image-container">
                  <v-img class="image" :src="ownerLogo" :contain="true" />
                </div>
                <div class="nameWrapper flex-col my-auto ml-2">
                  <span
                    class=""
                    style="line-height: 0.8rem"
                    :style="`color: ${colorOnHeader}`"
                    >{{ userProfile.name }} {{ userProfile.surnames }}</span
                  >
                  <span
                    v-if="userProfile.ownerPerson"
                    class="text--md"
                    :style="`color: ${colorSecondary}`"
                    >{{ userProfile.ownerPerson.name }}</span
                  >
                </div>
              </div>
            </div>
            <F2gIcon
              v-if="multiOwner && multiOwner.length > 1"
              id="profile-arrow"
              class="mr-2 cursor-pointer arrow"
              style="width: 25px; height: 25px"
              :width="36"
              :height="36"
              :stroke="colorOnHeader"
              :strokeWidth="4"
              value="arrow-right"
              @click="cardClickEvent('profile-arrow')"
            />

            <!-- Settings -->
            <div class="cardSettings-options flex-col-center">
              <div
                class="card__settings-item"
                @click="goTo('/s/settings')"
                :style="
                  settingsMenu[0].hover
                    ? 'background-color:' + colorPrimary
                    : 'background-color:' + colorSurface
                "
                @mouseover="setHoveredMenuStyle(true, 0)"
                @mouseleave="setHoveredMenuStyle(false, 0)"
              >
                <v-layout
                  align-center
                  class="py-1"
                  :style="
                    settingsMenu[0].hover
                      ? 'color:' + colorOnPrimary
                      : 'color:' + colorOnSurface
                  "
                >
                  <F2gIcon
                    class="mr-2 cursor-pointer"
                    style="width: 25px; height: 25px"
                    :stroke="setIconColor(0)"
                    :strokeWidth="4"
                    value="double-config"
                  />
                  <span
                    class="text-subtitle-1"
                    v-html="$t('common.settings')"
                  ></span>
                </v-layout>
              </div>

              <!-- Configuration -->
              <div
                v-if="
                  sidebarMenuStructure.length > 0 &&
                  sidebarMenuStructure[0].children.length > 0
                "
                class="card__settings-item"
                @click="goTo(sidebarMenuStructure[0].children[0].link)"
                :style="
                  settingsMenu[2].hover
                    ? 'background-color:' + colorPrimary
                    : 'background-color:' + colorSurface
                "
                @mouseover="setHoveredMenuStyle(true, 2)"
                @mouseleave="setHoveredMenuStyle(false, 2)"
              >
                <v-layout
                  align-center
                  :style="
                    settingsMenu[2].hover
                      ? 'color:' + colorOnPrimary
                      : 'color:' + colorOnSurface
                  "
                  class="py-1"
                >
                  <F2gIcon
                    class="mr-2 cursor-pointer"
                    style="width: 25px; height: 25px"
                    :stroke="setIconColor(2)"
                    :strokeWidth="4"
                    value="server"
                  />
                  <span
                    class="text-subtitle-1"
                    v-html="$t('common.configuration')"
                  ></span>
                </v-layout>
              </div>

              <!-- Log out -->
              <div
                class="card__settings-item"
                @click="logout"
                :style="
                  settingsMenu[3].hover
                    ? 'background-color:' + colorPrimary
                    : 'background-color:' + colorSurface
                "
                @mouseover="setHoveredMenuStyle(true, 3)"
                @mouseleave="setHoveredMenuStyle(false, 3)"
              >
                <v-layout
                  align-center
                  :style="
                    settingsMenu[3].hover
                      ? 'color:' + colorOnPrimary
                      : 'color:' + colorOnSurface
                  "
                  class="py-1"
                >
                  <F2gIcon
                    class="mr-2 cursor-pointer"
                    style="width: 25px; height: 25px"
                    :stroke="setIconColor(3)"
                    :strokeWidth="4"
                    value="power-off"
                  />
                  <span
                    class="text-subtitle-1"
                    v-html="$t('common.logout')"
                  ></span>
                </v-layout>
              </div>

              <!-- owner selector -->
              <!-- <div
              v-if="isSuperOwner"
              class="cardSettings-ownerSelector cursor-pointer"
              :style="
                settingsMenu[4].hover
                  ? 'background-color:' + colorPrimary
                  : 'background-color:' + colorSurface
              "
              @mouseover="setHoveredMenuStyle(true, 4)"
              @mouseleave="setHoveredMenuStyle(false, 4)"
            >
              <v-layout
                justify-space-between
                :style="
                  settingsMenu[4].hover
                    ? 'color:' + colorOnPrimary
                    : 'color:' + colorOnSurface
                "
                class="py-1"
              >
                <v-layout @click="showOwnerPicker()">
                  <F2gIcon
                    class="mr-2 mt-1 cursor-pointer"
                    style="width: 25px; height: 25px"
                    :stroke="setIconColor(4)"
                    :strokeWidth="4"
                    @click="showOwnerPicker"
                    value="user"
                  />
                  <span v-if="globalOwnerSelected.name" class="mt-1">
                    {{ globalOwnerSelected.name }}
                  </span>
                  <span v-else class="italicNoOwnerSelected mt-1">
                    {{ $t("common.selectOwner") }}
                  </span>
                </v-layout>
                <div v-if="globalOwnerSelected.name">
                  <F2gIcon
                    class="cursor-pointer pa-1"
                    style="width: 33px; height: 29px"
                    value="close"
                    :strokeWidth="4"
                    stroke="#FFFFFF"
                    @click="resetGlobalOwner()"
                  />
                </div>
              </v-layout>
            </div> -->
            </div>
          </v-layout>
        </v-card>
        <v-card
          v-if="showMultiOwnerPicker"
          elevation="8"
          class="card cardSettings button-rounded"
          :style="{
            'background-color': colorHeader,
            color: colorOnHeader,
          }"
        >
          <!-- Nombre +  icon = multiOwner -->
          <div
            v-for="(owner, index) in filterOwners"
            :key="owner.id"
            class="owner-card flex-columnn cursor-pointer truncate"
            :class="{
              separator: index != filterOwners.length - 1,
            }"
            :style="`background-color: ${colorHeader}`"
            @click="changeMultiOwnerAction(owner.idOwner, owner.active)"
          >
            <div class="flex-row cursor-pointer truncate">
              <div class="image-container">
                <v-img class="image" :src="owner.imgOwner" :contain="true" />
              </div>
              <div class="nameWrapper flex-col my-auto ml-2">
                <span
                  class=""
                  style="line-height: 0.8rem"
                  :style="`color: ${colorOnHeader}`"
                  >{{ owner.nameOwner }}</span
                >
                <span
                  v-if="owner.active"
                  class="text--md"
                  :style="`color: ${$CONSTANTS.COLOUR.GREEN_1_F2G}`"
                  >{{ $t("common.active") }}</span
                >
              </div>
            </div>
            <v-text-field
              v-if="owner.active"
              :placeholder="$t('common.search')"
              :hide-details="true"
              :background-color="f2gWhite"
              class="search-bar mx-auto"
              v-model="searchInput"
              rounded
              outlined
              clearable
              single-line
              dense
              :color="colorOnHeader"
              @click.stop
              @click:clear="() => (searchInput = '')"
            ></v-text-field>
          </div>
          <F2gIcon
            v-if="multiOwner && multiOwner.length > 1"
            id="profile-arrow-left"
            class="mr-2 cursor-pointer arrow"
            style="width: 25px; height: 25px"
            :width="36"
            :height="36"
            :stroke="setIconColor(0)"
            :strokeWidth="4"
            value="arrow-left"
            @click="cardClickEvent('profile-arrow-left')"
          />
        </v-card>
      </div>
    </transition>
    <!-- picker owner -->
    <!-- <DialogOwnerPicker
      :showDialog="showOwnerDialog"
      @closeDialog="closeOwnerDialog"
      @ownerSelected="setGlobalOwner"
    /> -->
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { directive as onClickaway } from "vue-clickaway";

import NoDataEnum from "@/flit2go-web-core/interfaces/no-data-enum";
import NotificationElemList from "@/flit2go-web-core/components/notifications/notification-elem-list";
import NoData from "@/flit2go-web-core/components/ui/no-data";
// import DialogOwnerPicker from "@/flit2go-web-core/components/dialog-pickers/dialog-global-owner-picker";

import { OWNERS_HOME } from "@/modules/Owners/routesInterface";
import { DEALER_HOME } from "@/modules/Dealer/routesInterface";
import { EventBus } from "@/flit2go-web-core/util/event-bus.js";

import menuStructureFile from "@/base/menuStructure/menuStructure.js";
import service from "@/flit2go-web-core/services/";
import {
  openZohoSalesIQChat,
  cancelZohoSalesIQChatRetry,
} from "@/util/openZohoSalesIQChat.js";
import zohoDeskAsap from "@/flit2go-web-core/services/zohoDeskAsapService.js";

const isManager = process.env.VUE_APP_TITLE === "Manager";
const isProduction = process.env.VUE_APP_MODE === "production";

import { PROFILE } from "@/base/routesInterface.js";

export default {
  name: "F2g-Toolbar",
  directives: {
    onClickaway: onClickaway,
  },
  components: {
    FirebaseNotifications: () =>
      import(
        "@/flit2go-web-core/components/notifications/firebase-notifications"
      ),
    NotificationElemList,
    NoData,
    // DialogOwnerPicker,
  },
  data: () => ({
    searchInput: "",
    menuStructureFile,
    sidebarMenuStructure: [],
    OWNERS_HOME,
    DEALER_HOME,
    NoDataEnum,
    showSettings: false,
    showNotifications: false,
    showOwnerDialog: false,
    showFloatingNotification: false,
    f2gDarkPrimary: "#334532",
    f2gLightGray2: "#eeeeee",
    f2gSurface: "#495848",
    f2gWhite: "#FFFFFF",
    f2gDarkGrey: "#555555",
    f2gDarkAccent: "#495848",
    f2gBlue: "#3bcdf2",
    colorOnPrimary: null,
    colorPrimary: null,
    colorHeader: null,
    colorOnHeader: null,
    colorSurface: null,
    colorSecondary: null,
    colorOnSurface: null,
    colorDot: null,
    ownerAppIcon: null,
    ownerLogo: null,
    isResetButton: false,
    isManager: isManager,
    settingsMenu: [
      { hover: false },
      { hover: false },
      { hover: false },
      { hover: false },
    ],
    globalOwnerHover: {},
    globalOwnerSelected: {},
    userSettings: {},
    showSnackbar: false,
    textToSnackbar: null,
    changeMultiOwner: false,
    showMultiOwnerPicker: false,
    multiOwner: null,
    arrowReturn: false,
    horizontalToolbar: () =>
      import("@/flit2go-web-core/components/layout/toolbar/horizontal-toolbar"),
    hamburguerToolbar: () =>
      import("@/flit2go-web-core/components/layout/toolbar/hamburguer-toolbar"),
  }),
  watch: {
    "$store.state.auth.userRole"() {
      this.initSidebarMenuConfiguration();
    },
  },
  computed: {
    ...mapState(["notifications"]),
    ...mapGetters({
      userProfile: "auth/getUserProfile",
      globalOwner: "auth/getGlobalOwner",
      isSuperOwner: "auth/isSuperOwner",
      ownerColors: "auth/getOwnerColors",
      ownerToolbarColors: "auth/getOwnerToolbarColors",
    }),
    filterOwners() {
      const filteredOwners = this.multiOwner.filter(
        (owner) =>
          (owner.nameOwner &&
            owner.nameOwner
              .toLowerCase()
              .includes(
                this.searchInput ? this.searchInput.toLowerCase() : "",
              )) ||
          owner.active,
      );
      return filteredOwners.sort((a, b) => b.active - a.active);
    },
    store: function () {
      return this.$store.state.auth.userProfile;
    },
    toolbarComponent() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
        case "sm":
          return this.hamburguerToolbar;
        case "md":
        default:
          return this.horizontalToolbar;
      }
    },
    showConfigurationToolbar() {
      if (
        menuStructureFile.hasReadPermission(
          this.$CONSTANTS.MODULE.CONFIGURATION,
        ) &&
        this.$route.path.includes("configuration") &&
        (this.$vuetify.breakpoint.name === "xs" ||
          this.$vuetify.breakpoint.name === "sm")
      ) {
        return true;
      } else {
        return false;
      }
    },
    mobileMode() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
        case "sm":
        case "md":
          return false;
        default:
          return true;
      }
    },
    hasPermsToChat() {
      return (
        isProduction &&
        isManager &&
        menuStructureFile.hasReadPermission(
          this.$CONSTANTS.MODULE.CONFIGURATION.CHAT,
        )
      );
    },
  },
  created() {
    this.getMultiOwner();
    this.setOwnerColors();
    EventBus.$on(
      "personalizationChanged",
      (showPersonalizationColorsTopbar) => {
        this.ownerToolbarColors.personalizationToolbar =
          showPersonalizationColorsTopbar;
        this.setOwnerColors();
      },
    );
    this.setInitialGlobalOwner();

    if (this.isManager && this.ownerColors.personalizationContent) {
      this.$vuetify.theme.themes.light.primary =
        this.ownerColors.ownerPrimaryColor;
    } else {
      this.$vuetify.theme.themes.light.primary =
        this.$CONSTANTS.COLOUR.GREEN_1_F2G;
    }
  },
  mounted() {
    this.initSidebarMenuConfiguration();
    setTimeout(() => {
      this.showFloatingNotification = true;
    }, 1200);
    // Intercom chat launcher, replaced by the Zoho SalesIQ chat opened from openSalesIQChat().
    // Kept here (not deleted) in case Intercom needs to be restored for #chatLauncher.
    // if (this.hasPermsToChat) {
    //   var w = window;
    //   var ic = w.Intercom;
    //   if (typeof ic === "function") {
    //     ic("reattach_activator");
    //     ic("update", w.intercomSettings);
    //   } else {
    //     var d = document;
    //     var i = function () {
    //       i.c(arguments);
    //     };
    //     i.q = [];
    //     i.c = function (args) {
    //       i.q.push(args);
    //     };
    //     w.Intercom = i;
    //     var l = function () {
    //       var s = d.createElement("script");
    //       s.type = "text/javascript";
    //       s.async = true;
    //       s.src = "https://widget.intercom.io/widget/qkyivjai";
    //       var x = d.getElementsByTagName("script")[0];
    //       x.parentNode.insertBefore(s, x);
    //     };
    //     if (document.readyState === "complete") {
    //       l();
    //     } else if (w.attachEvent) {
    //       w.attachEvent("onload", l);
    //     } else {
    //       w.addEventListener("load", l, false);
    //     }
    //   }
    //   window.Intercom("boot", {
    //     app_id: "qkyivjai",
    //     email: this.userProfile.username,
    //     user_id: this.userProfile.id,
    //     name: `${this.userProfile.name} ${this.userProfile.surnames}`,
    //     phone: this.userProfile.phoneNumber,
    //     language_override: this.$PageUtils.getLanguageSplittedMinus(),
    //     company: {
    //       company_id: this.userProfile.ownerPerson.id,
    //       name: this.userProfile.ownerPerson.name,
    //     },
    //     created_at: new Date().getTime(),
    //     hide_default_launcher: true,
    //     custom_launcher_selector: "#chatLauncher",
    //   });
    // }
  },
  destroyed() {
    cancelZohoSalesIQChatRetry();
  },
  methods: {
    openSalesIQChat() {
      openZohoSalesIQChat();
    },
    openHelpCenter() {
      zohoDeskAsap.openHelpCenter();
    },
    showMoreNotifications() {
      this.showNotifications = false;
      this.goTo(PROFILE);
    },
    showMoreNotificationsNewWindow() {
      this.showNotifications = false;
      window.open(`#${PROFILE}`, "_blank");
    },
    initSidebarMenuConfiguration() {
      if (
        this.$store.state.auth.userRole &&
        this.$store.state.auth.userRole.length > 0
      ) {
        this.sidebarMenuStructure = menuStructureFile.getSidebarConfiguration();
      }
    },
    closeOwnerDialog() {
      this.showOwnerDialog = false;
      this.showSettings = true;
    },
    cardClickEvent(action) {
      if (action === "profile-arrow") {
        this.changeMultiOwner = true;
      } else if (action === "profile-arrow-left") {
        this.showMultiOwnerPicker = false;
        this.showSettings = true;
        this.arrowReturn = true;
      } else {
        this.$router.push("/s/profile");
      }
    },
    getMultiOwner() {
      if (this.isManager) {
        this.$store
          .dispatch("auth/getMultiOwner")
          .then((resp) => {
            this.multiOwner = resp.data;
          })
          .catch(this.$showMessage.errorGeneric);
      }
    },
    changeMultiOwnerAction(idOwner, ownerActive = false) {
      if (idOwner && !ownerActive) {
        this.$store
          .dispatch("auth/changeMultiOwner", idOwner)
          .then(() => {
            service.auth
              .personByUsername(this.userProfile.username)
              .then(() => {
                this.$router.push(menuStructureFile.getFirstLinkAllowed());
                setTimeout(() => {
                  window.location.reload();
                }, 300);
                this.$showMessage.success(this.$t("common.multiOwnerChanged"));
              })
              .catch(this.$showMessage.showError);
          })
          .catch(this.$showMessage.showError);
      }
    },
    deleteGlobalOwner() {
      this.$store
        .dispatch("auth/RESET_GLOBAL_OWNER")
        .then(() => {
          this.$showMessage.info(this.$t("common.globalOwnerDeleted"));
        })
        .catch(() => {
          this.$showMessage.error(this.$t(this.$CONSTANTS.ERROR.REQUIRED));
        });
    },
    goTo(route) {
      this.$router.push(route);
    },
    goToHomeView() {
      this.$router.push(this.menuStructureFile.getFirstLinkAllowed());
    },
    goToHomeViewNewWindow() {
      window.open(`#${this.menuStructureFile.getFirstLinkAllowed()}`, "_blank");
    },
    setHoveredMenuStyle(hover, position) {
      this.settingsMenu[position].hover = hover;
    },
    setGlobalOwner(owner) {
      this.globalOwnerSelected = owner;
    },
    setHoveredOwnerSelectorStyle(hover) {
      this.globalOwnerHover = { hover: hover };
    },
    toggleCardSettings() {
      this.showSettings = !this.showSettings;
      this.showMultiOwnerPicker = false;
      this.showNotifications = false;
      this.showCardCreation = false;
    },
    closeCardSettings() {
      if (!this.isResetButton) {
        if (this.changeMultiOwner) {
          this.showSettings = false;
          this.showMultiOwnerPicker = true;
          this.changeMultiOwner = false;
        } else {
          this.showMultiOwnerPicker = false;
          if (!this.arrowReturn) {
            this.showSettings = false;
          }
          this.arrowReturn = false;
        }
      }
    },
    setOwnerColors() {
      if (this.ownerToolbarColors.personalizationToolbar) {
        this.colorHeader = this.ownerToolbarColors.ownerHeaderColor;
        this.colorOnHeader = this.ownerToolbarColors.ownerOnHeaderColor;
        this.colorPrimary = this.ownerToolbarColors.ownerPrimaryColor;
        this.colorOnPrimary = this.ownerToolbarColors.ownerOnPrimaryColor;
        this.colorSecondary = this.ownerToolbarColors.ownerSecondaryColor;
        this.colorSurface = this.ownerToolbarColors.ownerSurfaceColor;
        this.colorOnSurface = this.ownerToolbarColors.ownerOnSurfaceColor;
        this.colorDot = this.ownerToolbarColors.ownerNotificationDotColor;
        this.ownerAppIcon = this.ownerToolbarColors.ownerAppIcon;
      } else {
        this.colorOnPrimary = "#334532";
        this.colorPrimary = "#ffffff";
        this.colorHeader = "#334532";
        this.colorOnHeader = "#eeeeee";
        this.colorSurface = "#495848";
        this.onSurfaceColor = "#eeeeee";
        this.colorSecondary = "#eeeeee";
        this.colorOnSurface = "#eeeeee";
        this.colorDot = "#3bcdf2";
        this.ownerAppIcon = require("@/flit2go-web-core/assets/logo_simple_b_n.svg");
      }
      if (this.isManager) {
        this.ownerLogo = this.ownerToolbarColors.ownerLogo;
      } else {
        this.ownerLogo = require("@/flit2go-web-core/assets/flit2goLogo.svg");
      }
    },
    showCardNotifications() {
      if (this.showSettings) {
        this.showSettings = false;
        this.showMultiOwnerPicker = false;
      }
      if (this.showNotifications) {
        this.showNotifications = false;
        this.$emit("cardOpened", false);
      } else {
        this.showNotifications = true;
        this.$emit("cardOpened", true);
      }
    },
    showOwnerPicker() {
      this.showOwnerDialog = true;
      this.showMultiOwnerPicker = false;
      this.showSettings = false;
    },
    logout() {
      this.$store.dispatch("auth/AUTH_LOGOUT").then(() => {
        this.goTo("/login");
      });
    },
    removeNotification(notification) {
      this.$store.dispatch("notifications/markNotiAsViewed", {
        notiId: notification.id,
        viewed: true,
      });
      this.$store.commit("notifications/REMOVE_NOTIFICATION", notification);
    },
    resetGlobalOwner() {
      this.globalOwnerSelected = {};
      this.deleteGlobalOwner();
      this.isResetButton = true;
      setTimeout(() => (this.isResetButton = false), 100);
      this.showCreate = true;
    },
    setIconColor(position) {
      if (this.settingsMenu[position].hover) {
        return this.colorOnPrimary;
      } else {
        return this.colorOnSurface;
      }
    },
    setInitialGlobalOwner() {
      if (this.globalOwner) {
        this.globalOwnerSelected = this.globalOwner;
      } else {
        this.globalOwnerSelected = {};
      }
    },
    // Required for clickaway
    closeNotificationList() {
      this.showNotifications = false;
    },
    closeSettingsList() {
      this.showNotifications = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.arrow {
  z-index: 2147483647;
  position: absolute;
  right: 10px;
  top: 28px;
}

.notificationsWrapper {
  padding: 0 2rem;
  border: 2px solid $f2g-light-gray-2;
  border-bottom: 0;
  border-top: 0;
}

.v-toolbar {
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 56;
  color: $f2g-light-gray-2;
  background-color: $f2g-dark-primary;
}

.image-container {
  border: 1px solid black;
  border-radius: 90%;
  height: 50px;
  width: 50px;
}

.image {
  height: 100%;
  // width: 100%;
  border-radius: 90%;
  background-color: white;
}

.card {
  position: fixed;
  z-index: 2147483647;
  top: 70px;
  right: 5px;
}

.italicNoOwnerSelected {
  font-style: italic;
}

.cardSettings {
  width: 340px;
  max-height: 340px;
  overflow-y: auto;

  .cardSettings-username {
    padding: 18px 24px;
    border-bottom: 2px solid $lighten-gray-f2g;
  }
  .owner-card {
    padding: 18px 24px;
  }

  .separator {
    border-bottom: 2px solid $lighten-gray-f2g;
  }

  .search-bar {
    margin-top: 15px;
  }

  .cardSettings-ownerSelector {
    border-top: 2px solid $lighten-gray-f2g;
    padding: 10px 24px;
    transition: background-color;
    transition-duration: 250ms;
  }

  .cardSettings-options {
    .card__settings-item {
      padding: 10px 24px;
      transition: background-color;
      transition-duration: 250ms;

      cursor: pointer;
    }
  }
  a {
    stroke: $f2g-light-gray-2 !important;
    color: $f2g-light-gray-2 !important;
    background-color: $f2g-dark-primary !important;
    text-decoration: none !important;
  }

  /* width */
  &::-webkit-scrollbar {
    width: 5px;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: white;
    border-radius: 30px;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: white;
  }
}

// Notis
.card--notifications {
  border-radius: 10px;
  padding: 0 10px;

  width: 400px;
  height: fit-content;
  max-height: 80vh;
  overflow-y: scroll;
}

.card--notifications::-webkit-scrollbar {
  display: none;
}

.card--notifications::-webkit-scrollbar-track {
  background: $f2g-dark-primary;
}

/* Handle */
.card--notifications::-webkit-scrollbar-thumb {
  background: $f2g-dark-primary;
}

.showMore {
  width: 100%;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
}
</style>
